import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Devices, EventTriggers, Facilities, FacilityGroups } from "@app/shared/src/api/api.gen";
import { Device, EventTrigger, FacilityGroup, OperationMode } from "@app/shared/src/models";
import { siteLabel } from "../../helpers/deviceLabels";
import { usePoll } from "../../hooks/usePoll";
import { useOrg } from "./OrgProvider";

/**
 * The org's device roster, scoped to the facility group the console is looking at.
 *
 * Nearly every page needs the fleet — nav counts, unit pickers, the DVR fan-out, name lookups —
 * and each fetching its own copy meant four identical requests per navigation. Triggers are the
 * same story one level down (zone names, alert defaults, notification recipients all live on
 * them), but they cost one request per facility, so they load lazily on first use and then stay.
 *
 * Devices carry a facility, not a group, so the group a truck belongs to is resolved through its
 * facility. That mapping is fetched alongside the roster and is what both the group picker and the
 * stats scope are built from.
 */

/** How often the roster re-reads itself; see usePoll for why a console needs this at all. */
const REFRESH_MS = 60_000;
const GROUP_KEY = "tb.groupId";
/** The picker's value for "don't filter" — a select needs a string, the state uses undefined. */
export const ALL_GROUPS = "all";

type FleetState = {
    /** The fleet as the selected group scopes it — what the pages list. */
    devices: Device[];
    /** Every device in the organization, whatever the group filter currently says. */
    allDevices: Device[];
    /**
     * The devices the fleet's numbers are about: attenuator units, plus anything sharing a group
     * with one. Still inside the selected group.
     */
    statsDevices: Device[];
    /** The groups worth offering — see the memo below for what earns a place. */
    groups: FacilityGroup[];
    /** The selected group, or undefined for "All groups". */
    group?: FacilityGroup;
    setGroupId: (id?: string) => void;
    isLoading: boolean;
    error?: string;
    reload: () => Promise<void>;
    deviceName: (id?: string) => string;
    /** The TMA/site a unit sits at — the name a director reads a list by. See helpers/deviceLabels. */
    deviceSite: (id?: string) => string;
    /** The key printed on that unit's label — the tiebreaker under every site name. */
    deviceKey: (id?: string) => string | undefined;
    /** Triggers per facilityId — resolves cached after the first call. */
    loadTriggers: () => Promise<Map<string, EventTrigger[]>>;
    /**
     * Drop the trigger cache so the next loadTriggers refetches. Call after writing a trigger:
     * without this, every consumer keeps reading the pre-save values until the org changes —
     * which is how a save could "stick" on screen (dirty bar never clearing, reset never showing).
     */
    invalidateTriggers: () => void;
};

const FleetContext = createContext<FleetState | undefined>(undefined);

export function useFleet(): FleetState {
    const value = useContext(FleetContext);
    if (!value) throw new Error("useFleet must be used inside FleetProvider");
    return value;
}

/** What "a TMA attenuator unit" means everywhere in the console. */
export const isAttenuator = (d: Device) => d.mode === OperationMode.Attenuator_Truck;

export const FleetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { org } = useOrg();
    const [allDevices, setAllDevices] = useState<Device[]>([]);
    const [allGroups, setAllGroups] = useState<FacilityGroup[]>([]);
    /** facilityId → facilityGroupId: the only part of a facility the console needs. */
    const [facilityGroupIds, setFacilityGroupIds] = useState<Map<string, string>>(new Map());
    const [groupId, setGroupIdState] = useState<string | undefined>(() => localStorage.getItem(GROUP_KEY) ?? undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>();
    const seq = useRef(0);
    const triggersRef = useRef<{ orgId: string; promise: Promise<Map<string, EventTrigger[]>> }>();

    const load = useCallback(
        async (quiet: boolean) => {
            if (!org?.id) return;
            const mySeq = ++seq.current;
            if (!quiet) setIsLoading(true);
            try {
                const [devices, facilities, groups] = await Promise.all([
                    Devices.getByOrganizationId(org.id),
                    // Grouping is a filter, not the fleet. If either of these fails the roster
                    // still stands and the picker simply doesn't appear — losing every truck on
                    // screen because a facility list errored is the wrong way round.
                    Facilities.getActiveByOrganizationId(org.id).catch(() => undefined),
                    FacilityGroups.getByOrganizationId(org.id).catch(() => undefined),
                ]);
                // A slower earlier request landing after a newer one would put another org's fleet
                // on screen under this org's name.
                if (seq.current !== mySeq) return;
                setAllDevices(devices ?? []);
                setFacilityGroupIds(
                    new Map(
                        (facilities ?? [])
                            .filter((f) => f.id && f.facilityGroupId)
                            .map((f) => [f.id!, f.facilityGroupId!] as const),
                    ),
                );
                // Without the facility→group mapping every group would look empty, and picking one
                // would silently show nothing. No mapping, no picker.
                setAllGroups(facilities ? (groups ?? []).filter((g) => !g.isDeleted) : []);
                setError(undefined);
            } catch (err) {
                if (seq.current === mySeq) setError(err instanceof Error ? err.message : "Could not load the fleet");
            } finally {
                if (seq.current === mySeq) setIsLoading(false);
            }
        },
        [org?.id],
    );

    const invalidateTriggers = useCallback(() => {
        triggersRef.current = undefined;
    }, []);

    // The explicit reload — what sections call after a save — also drops the trigger cache, since
    // a save is exactly when the cached triggers went stale. The quiet 60s poll does not: it would
    // turn the lazy one-request-per-facility cache into a per-minute refetch of the whole org.
    const reload = useCallback(() => {
        triggersRef.current = undefined;
        return load(false);
    }, [load]);

    useEffect(() => {
        setAllDevices([]);
        triggersRef.current = undefined;
        load(false);
    }, [load]);

    usePoll(() => load(true), REFRESH_MS);

    const groupIdOf = useCallback(
        (d: Device) => (d.facilityId ? facilityGroupIds.get(d.facilityId) : undefined),
        [facilityGroupIds],
    );

    /**
     * Which groups the picker offers: every group the organization has.
     *
     * This used to hide any group whose units were all another product line (a group of parking
     * counters is a dead end from an attenuator console), but the effect was a picker that silently
     * vanished — on an org whose groups hold no trucks yet there was no way to choose a group at
     * all, and nothing said why. A group that leads to an empty fleet now reads as exactly that:
     * the pages name the group in their empty state, which is a better answer than no picker.
     */
    const groups = useMemo(
        () => allGroups.filter((g) => g.id).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
        [allGroups],
    );

    // A stored group from another org — or one that has since dropped off the list — reads as
    // "All groups" rather than as an empty fleet with no way to tell why.
    const group = useMemo(() => groups.find((g) => g.id === groupId), [groups, groupId]);

    const setGroupId = useCallback((id?: string) => {
        const next = !id || id === ALL_GROUPS ? undefined : id;
        if (next) localStorage.setItem(GROUP_KEY, next);
        else localStorage.removeItem(GROUP_KEY);
        setGroupIdState(next);
    }, []);

    const devices = useMemo(
        () => (group ? allDevices.filter((d) => groupIdOf(d) === group.id) : allDevices),
        [allDevices, group, groupIdOf],
    );

    /**
     * The fleet the numbers are about.
     *
     * An attenuator unit always counts. So does anything sharing a group with one, because a
     * second sensor on the same crew is part of that crew's day. What this leaves out is the rest
     * of the organization — a traffic counter on a gantry somewhere — which would otherwise sit in
     * every gauge on Overview as a truck that is permanently offline and never tests its relays.
     */
    const statsDevices = useMemo(() => {
        const attenuatorGroups = new Set(
            allDevices
                .filter(isAttenuator)
                .map(groupIdOf)
                .filter((gid): gid is string => Boolean(gid)),
        );
        return devices.filter((d) => {
            if (isAttenuator(d)) return true;
            const gid = groupIdOf(d);
            return gid !== undefined && attenuatorGroups.has(gid);
        });
    }, [devices, allDevices, groupIdOf]);

    // Names resolve against the whole org: an event list can hold a truck the current group
    // filters out, and "—" there would read as missing data rather than as a filter.
    const deviceName = useCallback((id?: string) => allDevices.find((d) => d.id === id)?.name ?? "—", [allDevices]);
    const deviceSite = useCallback((id?: string) => siteLabel(allDevices.find((d) => d.id === id)), [allDevices]);
    const deviceKey = useCallback((id?: string) => allDevices.find((d) => d.id === id)?.deviceKey, [allDevices]);

    const loadTriggers = useCallback((): Promise<Map<string, EventTrigger[]>> => {
        const orgId = org?.id ?? "";
        if (triggersRef.current?.orgId === orgId) return triggersRef.current.promise;
        // Org-wide rather than group-scoped: zone lookups are by id, so extra entries cost nothing
        // and this way the cache survives a change of group.
        const facilityIds = Array.from(new Set(allDevices.map((d) => d.facilityId).filter(Boolean))) as string[];
        const promise = (async () => {
            const map = new Map<string, EventTrigger[]>();
            await Promise.all(
                facilityIds.map(async (fid) => {
                    try {
                        map.set(fid, (await EventTriggers.getByFacilityId(orgId, fid)) ?? []);
                    } catch {
                        map.set(fid, []);
                    }
                }),
            );
            return map;
        })();
        triggersRef.current = { orgId, promise };
        return promise;
    }, [org?.id, allDevices]);

    const value = useMemo(
        () => ({
            devices,
            allDevices,
            statsDevices,
            groups,
            group,
            setGroupId,
            isLoading,
            error,
            reload,
            deviceName,
            deviceSite,
            deviceKey,
            loadTriggers,
            invalidateTriggers,
        }),
        [
            devices,
            allDevices,
            statsDevices,
            groups,
            group,
            setGroupId,
            isLoading,
            error,
            reload,
            deviceName,
            deviceSite,
            deviceKey,
            loadTriggers,
            invalidateTriggers,
        ],
    );

    return <FleetContext.Provider value={value}>{children}</FleetContext.Provider>;
};
